import React, { useState } from 'react';
import { Env, ENV_COLORS, SuccessBanner } from '../shared';
import { CodeList } from './components/CodeList';
import { CodeForm } from './components/CodeForm';
import { CodeDashboard } from './components/CodeDashboard';
import { CampaignList } from './components/CampaignList';
import { CampaignForm } from './components/CampaignForm';
import { SettingsPanel } from './components/SettingsPanel';
import { DateRange, defaultDateRange } from './components/controls';
import { PromoCampaign, ReferralCode } from './api/types';

type Section = 'codes' | 'campaigns' | 'settings';

type View =
  | { mode: 'list' }
  | { mode: 'create-code' }
  | { mode: 'edit-code'; code: ReferralCode }
  | { mode: 'view-code'; code: string }
  | { mode: 'create-campaign' }
  | { mode: 'edit-campaign'; campaign: PromoCampaign };

/**
 * `/admin/referrals/*` only ships prod and dev twins (see
 * infra/sam/api-referrals-admin) — there is no staging stage to point at, so the
 * selector offers the two that exist.
 */
const ENVS: Env[] = ['dev', 'prod'];

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'codes', label: 'Codes & dashboard' },
  { id: 'campaigns', label: 'Deal campaigns' },
  { id: 'settings', label: 'Settings' },
];

/**
 * Referral Codes section (SSA-955). Replaces the Bar Outreach Leaderboard as the
 * team's referral view: mint and manage promoter / campaign codes, build the deal
 * campaigns they attach to, and read the per-code dashboard — verified signups,
 * $ owed, redemptions and the review-flagged rows behind them.
 *
 * Every route sits behind MSAL/Entra and the `referral_codes_enabled` AppConfig
 * gate; a 503 `feature_disabled` from any panel means the flag is off for that
 * environment, not that the section is broken.
 *
 * Defaults to dev because this screen mints codes and edits the fallback deal —
 * prod is one deliberate click away, with a banner.
 */
export function ReferralCodesTab() {
  const [env, setEnv] = useState<Env>('dev');
  const [section, setSection] = useState<Section>('codes');
  const [view, setView] = useState<View>({ mode: 'list' });
  const [range, setRange] = useState<DateRange>(defaultDateRange);
  const [flash, setFlash] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const backToList = () => setView({ mode: 'list' });

  const switchSection = (next: Section) => {
    setSection(next);
    setView({ mode: 'list' });
    setFlash(null);
  };

  const switchEnv = (next: Env) => {
    setEnv(next);
    setView({ mode: 'list' });
    setFlash(null);
  };

  const afterCodeSave = (code: ReferralCode, message: string) => {
    setFlash(message);
    setRefreshKey((k) => k + 1);
    setView({ mode: 'view-code', code: code.code });
  };

  const afterCampaignSave = (_campaign: PromoCampaign, message: string) => {
    setFlash(message);
    setRefreshKey((k) => k + 1);
    backToList();
  };

  return (
    <div className="space-y-5">
      {/* Header: section nav + env selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-1">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => switchSection(s.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                section === s.id
                  ? 'bg-violet-950/40 text-violet-300 border border-violet-500/60'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Environment</span>
          <div className="flex gap-1">
            {ENVS.map((e) => (
              <button
                key={e}
                onClick={() => switchEnv(e)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md border uppercase ${
                  env === e
                    ? ENV_COLORS[e].pill
                    : 'bg-gray-900 border-gray-800 text-gray-500 hover:text-gray-300'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      </div>

      {env === 'prod' && (
        <div className="px-3 py-2 bg-red-950/30 border border-red-900/40 rounded-lg text-xs text-red-300">
          Acting on <b>production</b>. Codes minted here pay real promoters, and editing the fallback deal
          changes what every un-attached code grants.
        </div>
      )}

      {flash && (
        <div className="relative">
          <SuccessBanner message={flash} />
          <button
            type="button"
            onClick={() => setFlash(null)}
            className="absolute right-3 top-3 text-emerald-400/70 hover:text-emerald-200"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {section === 'codes' && (
        <>
          {view.mode === 'list' && (
            <CodeList
              env={env}
              range={range}
              onRangeChange={setRange}
              onOpenCode={(c) => setView({ mode: 'view-code', code: c.code })}
              onCreate={() => setView({ mode: 'create-code' })}
              onEdit={(c) => setView({ mode: 'edit-code', code: c })}
              refreshKey={refreshKey}
            />
          )}

          {view.mode === 'create-code' && (
            <CodeForm env={env} onCancel={backToList} onSaved={afterCodeSave} />
          )}

          {view.mode === 'edit-code' && (
            <CodeForm env={env} existing={view.code} onCancel={backToList} onSaved={afterCodeSave} />
          )}

          {view.mode === 'view-code' && (
            <CodeDashboard
              env={env}
              code={view.code}
              range={range}
              onRangeChange={setRange}
              onBack={backToList}
              onEdit={(c) => setView({ mode: 'edit-code', code: c })}
            />
          )}
        </>
      )}

      {section === 'campaigns' && (
        <>
          {view.mode === 'list' && (
            <CampaignList
              env={env}
              onCreate={() => setView({ mode: 'create-campaign' })}
              onEdit={(c) => setView({ mode: 'edit-campaign', campaign: c })}
              refreshKey={refreshKey}
            />
          )}
          {view.mode === 'create-campaign' && (
            <CampaignForm env={env} onCancel={backToList} onSaved={afterCampaignSave} />
          )}
          {view.mode === 'edit-campaign' && (
            <CampaignForm
              env={env}
              existing={view.campaign}
              onCancel={backToList}
              onSaved={afterCampaignSave}
            />
          )}
        </>
      )}

      {section === 'settings' && <SettingsPanel env={env} refreshKey={refreshKey} />}
    </div>
  );
}
